// Console D's waterfall (the mock's perf.js `waterfall` and network.js `traces/:id`): spans on one
// time axis. `load` is a screen load: each span's name over its thread or host, its bar, its
// duration and its links, with the load's TTID and TTFD as dashed lines. `trace` is a trace:
// group rows, spans indented by depth with a status, and a selected row that drives the page's
// attributes panel (a click, or j and k).
import { type CSSProperties, type ReactNode, useEffect, useRef } from 'react'
import type { Translator } from '../../i18n'

export interface WaterfallSpan {
  key: string
  /** A telemetry name: never translated. */
  name: ReactNode
  /** The thread or host under the name (`load`), the target beside it (`trace`). */
  sub?: ReactNode
  /** Milliseconds from the axis' 0. */
  start: number
  /** Milliseconds; 0 draws an instant (a crash's mark). */
  duration: number
  /** Nesting under the root (`trace`). */
  depth?: number
  /** The bar's look: the load itself, a request, a slow span, a hang, a crash's instant. */
  bar?: 'root' | 'http' | 'slow' | 'hang' | 'crash' | undefined
  /** The status column (`trace`). */
  status?: ReactNode
  /** The duration column. */
  value: ReactNode
  /** Link tags at the row's end (`load`). */
  links?: ReactNode
  /** A span the team's backend sent over OTLP (`trace`, APS-507): muted, its bar outlined. */
  backend?: boolean
}

export interface WaterfallGroup {
  key: string
  group: ReactNode
  note?: ReactNode
  /** The backend's group (`trace`, APS-507). */
  backend?: boolean
}

export type WaterfallItem = WaterfallSpan | WaterfallGroup

const isGroup = (x: WaterfallItem): x is WaterfallGroup => 'group' in x

const STEPS = [1, 2, 5, 10, 20, 25, 50, 100, 200, 250, 500, 1000, 2000, 2500, 5000, 10_000]

/** Round ticks over a span of `total` ms: 0, then four or five even steps to the axis' end. */
export function axisTicks(total: number): number[] {
  const t = Math.max(total, 1)
  const step = STEPS.find((s) => t / s <= 5) ?? Math.ceil(t / 5 / 10_000) * 10_000
  const top = Math.ceil(t / step) * step
  const ticks: number[] = []
  for (let v = 0; v <= top; v += step) ticks.push(v)
  return ticks
}

/** An axis tick: 0, 250 ms, 1.5 s. */
export function tickLabel(i18n: Translator, v: number): string {
  if (v === 0) return '0'
  return v < 1000
    ? `${i18n.number(Math.round(v), { useGrouping: false })} ms`
    : `${i18n.number(v / 1000, { maximumFractionDigits: 1 })} s`
}

export function Waterfall({
  variant,
  label,
  items,
  ticks,
  i18n,
  marks,
  heads,
  selected,
  onSelect,
}: {
  variant: 'load' | 'trace'
  /** What the spans are, for screen readers. */
  label: string
  items: WaterfallItem[]
  /** From axisTicks: the last is the axis' end. */
  ticks: number[]
  i18n: Translator
  /** Dashed lines across the rows with a label in the head (`load`): TTID, TTFD. */
  marks?: { at: number; label: string }[]
  /** The head row's column names (`trace`). */
  heads?: { span: string; status: string; duration: string }
  /** The selected span's key (`trace`). */
  selected?: string | undefined
  onSelect?: ((key: string) => void) | undefined
}) {
  const total = ticks.at(-1) || 1
  const pct = (v: number) => `${Math.max(0, Math.min(100, (v / total) * 100)).toFixed(2)}%`
  const spans = items.filter((x): x is WaterfallSpan => !isGroup(x))
  // Marks far apart label inward, as the mock: the first after its line, the last before it.
  // Close together, one label names them all, on the side of the axis with room for it.
  const sorted = [...(marks ?? [])].sort((a, b) => a.at - b.at)
  const first = sorted[0]
  const last = sorted.at(-1)
  const close = !!first && !!last && sorted.length > 1 && (last.at - first.at) / total < 0.3
  const labels: { key: string; at: number; text: string; cls: string }[] =
    close && first && last
      ? [
          first.at / total > 0.5
            ? {
                key: 'all',
                at: first.at,
                text: sorted.map((m) => m.label).join(' · '),
                cls: 'pf-r',
              }
            : {
                key: 'all',
                at: last.at,
                text: sorted.map((m) => m.label).join(' · '),
                cls: 'pf-l',
              },
        ]
      : sorted.map((m, i) => ({
          key: m.label,
          at: m.at,
          text: m.label,
          // Inward when there are two; a lone mark labels toward the axis' middle.
          cls:
            sorted.length > 1
              ? i > 0 && i === sorted.length - 1
                ? 'pf-r'
                : 'pf-l'
              : m.at / total > 0.5
                ? 'pf-r'
                : 'pf-l',
        }))
  const ref = useRef<HTMLUListElement>(null)

  // j and k move the selection, as in a table (the mock's keys()).
  const keys = useRef({ spans, selected, onSelect })
  keys.current = { spans, selected, onSelect }
  useEffect(() => {
    if (!onSelect) return
    const h = (e: KeyboardEvent) => {
      if (e.key !== 'j' && e.key !== 'k') return
      if (e.metaKey || e.ctrlKey || e.altKey) return
      const target = e.target
      if (target instanceof Element && target.closest('input, textarea, select, [contenteditable]'))
        return
      const { spans: list, selected: cur, onSelect: pick } = keys.current
      if (!pick || list.length === 0) return
      const i = list.findIndex((s) => s.key === cur)
      const next = list[Math.max(0, Math.min(list.length - 1, i + (e.key === 'j' ? 1 : -1)))]
      if (!next) return
      e.preventDefault()
      pick(next.key)
      ref.current
        ?.querySelector<HTMLElement>(`[data-span="${CSS.escape(next.key)}"]`)
        ?.scrollIntoView({ block: 'nearest' })
    }
    document.addEventListener('keydown', h)
    return () => document.removeEventListener('keydown', h)
  }, [onSelect])

  if (variant === 'load') {
    const cols = (t: ReactNode) => (
      <>
        <span className="pf-wn" />
        <span className="pf-wt">{t}</span>
        <span className="pf-wd" />
        <span className="pf-wl" />
      </>
    )
    return (
      <ul className="pf-wf" aria-label={label} ref={ref}>
        {marks?.length ? (
          <>
            <li className="pf-wov" aria-hidden="true">
              {cols(marks.map((m) => <b key={m.label} style={{ left: pct(m.at) }} />))}
            </li>
            <li className="pf-wr pf-wax" aria-hidden="true">
              {cols(
                labels.map((m) => (
                  <span key={m.key} className={m.cls} style={{ left: pct(m.at) }}>
                    {m.text}
                  </span>
                )),
              )}
            </li>
          </>
        ) : null}
        {spans.map((s) => (
          <li className="pf-wr" key={s.key}>
            <span className="pf-wn">
              <b translate="no">{s.name}</b>
              {s.sub ? <span translate="no">{s.sub}</span> : null}
            </span>
            <span className="pf-wt" aria-hidden="true">
              <i
                className={
                  s.bar === 'root'
                    ? 'pf-root'
                    : s.bar === 'slow' || s.bar === 'hang'
                      ? 'pf-slow'
                      : s.bar === 'crash'
                        ? 'pf-x'
                        : undefined
                }
                style={{
                  left: pct(s.start),
                  width: s.bar === 'crash' ? undefined : pct(s.duration),
                }}
              />
            </span>
            <span className="pf-wd">{s.value}</span>
            <span className="pf-wl">{s.links}</span>
          </li>
        ))}
        <li className="pf-wr pf-wfoot" aria-hidden="true">
          {cols(
            ticks.map((v) => (
              <span key={v} style={{ left: pct(v) }}>
                {tickLabel(i18n, v)}
              </span>
            )),
          )}
        </li>
      </ul>
    )
  }

  const step = ticks.length > 1 ? (ticks[1] as number) : total
  const grid = { backgroundSize: `${(step / total) * 100}% 100%` } as CSSProperties
  return (
    <ul className="nw-wf" aria-label={label} ref={ref}>
      <li className="nw-wax" aria-hidden="true">
        <span>{heads?.span}</span>
        <span>{heads?.status}</span>
        <span className="du">{heads?.duration}</span>
        <div className="ax">
          {ticks.map((v) => (
            <span key={v} style={{ left: pct(v) }}>
              {tickLabel(i18n, v)}
            </span>
          ))}
        </div>
      </li>
      {items.map((x) =>
        isGroup(x) ? (
          <li className={x.backend ? 'nw-wg be' : 'nw-wg'} key={x.key}>
            {x.group}
            {x.note ? <em>{x.note}</em> : null}
          </li>
        ) : (
          <li key={x.key}>
            <button
              type="button"
              className={['nw-wr', x.backend ? 'be' : '', x.key === selected ? 'cur' : '']
                .filter(Boolean)
                .join(' ')}
              aria-pressed={onSelect ? x.key === selected : undefined}
              data-span={x.key}
              style={{ '--d': x.depth ?? 0 } as CSSProperties}
              onClick={() => onSelect?.(x.key)}
            >
              <span className="nm" translate="no">
                {x.name}
                {x.sub ? (
                  <>
                    {' '}
                    <span>{x.sub}</span>
                  </>
                ) : null}
              </span>
              <span className="sc">{x.status}</span>
              <span className="du">{x.value}</span>
              <span className="nw-tl" style={grid} aria-hidden="true">
                {x.bar === 'crash' ? (
                  <i className="nw-x" style={{ left: pct(x.start) }} />
                ) : (
                  <i
                    className={['nw-b', x.bar ?? '', x.backend ? 'be' : '']
                      .filter(Boolean)
                      .join(' ')}
                    style={{ left: pct(x.start), width: pct(x.duration) }}
                  />
                )}
              </span>
            </button>
          </li>
        ),
      )}
    </ul>
  )
}

/** Shares of a whole as bars (the mock's `.share` rows): a label, its count, a bar, a percent. */
export function ShareBars({
  label,
  rows,
}: {
  label: string
  rows: {
    key: string
    name: ReactNode
    n: ReactNode
    share: number
    value: string
    bad?: boolean
  }[]
}) {
  return (
    <ul aria-label={label}>
      {rows.map((r) => (
        <li key={r.key} className="share">
          <span>
            {r.name} <span className="dim">· {r.n}</span>
          </span>
          <span className="b" aria-hidden="true">
            <i
              className={r.bad ? 'bad' : undefined}
              style={{ width: `${Math.max(0, Math.min(1, r.share)) * 100}%` }}
            />
          </span>
          <span>{r.value}</span>
        </li>
      ))}
    </ul>
  )
}
